import { useId } from 'react';
import {
  kindLabel,
  statusLabel,
  type AgentWorkOrderDto,
  type AgentWorkOrderForm,
  type AgentWorkOrderKind,
  type AgentWorkOrderOptions,
} from '@/lib/agent-work-orders';
import { Badge, type BadgeTone } from './ui';
import { CardTable, TBody, THead, Td, Th, Tr } from './CardTable';

/** Aviso quando nenhum agente pode receber a OS: o estado de todo ambiente enquanto a escrita estiver fechada. */
export const NO_RECIPIENT_MESSAGE =
  'Nenhum agente pode receber esta ordem de serviço neste ambiente. A escrita pelo MCP ainda não foi liberada aqui.';

interface AgentWorkOrderFieldsProps {
  options: AgentWorkOrderOptions;
  form: AgentWorkOrderForm;
  onChange: (form: AgentWorkOrderForm) => void;
  /** Só os tipos que podem sair em lote ("Lojas para agir"). */
  batch?: boolean;
  disabled?: boolean;
}

/**
 * Campos da ordem de serviço (change platform-mcp-writes, 1.17), pré-preenchidos com o mínimo pela
 * página (`minimumForm`): tipo, agente destinatário, modo, máximo de dias, efeitos e validade. O
 * modo direto aparece desabilitado: exige passkey e ainda não existe. Nenhum campo recebe nome de
 * loja: o alvo vem da página ou das linhas marcadas.
 */
export function AgentWorkOrderFields({ options, form, onChange, batch = false, disabled = false }: AgentWorkOrderFieldsProps) {
  const id = useId();
  const kinds = options.kinds.filter((k) => !batch || k.batchable);
  const kind = kinds.find((k) => k.kind === form.kind);
  const recipients = options.recipients.filter((r) => r.kinds.includes(form.kind));

  function changeKind(next: AgentWorkOrderKind) {
    // Trocar o tipo volta os máximos ao mínimo e escolhe o primeiro agente que aceita o tipo novo.
    const recipient = options.recipients.find((r) => r.kinds.includes(next));
    onChange({ ...form, kind: next, agentClientId: recipient?.id ?? '', maxDays: 1, maxEffects: 1 });
  }

  return (
    <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="field">
        <label htmlFor={`${id}-kind`}>Tipo</label>
        <select id={`${id}-kind`} value={form.kind} onChange={(e) => changeKind(e.target.value as AgentWorkOrderKind)}>
          {kinds.map((k) => (
            <option key={k.kind} value={k.kind}>{kindLabel(k.kind)}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={`${id}-agent`}>Agente destinatário</label>
        {recipients.length === 0 ? (
          <div className="info-banner" role="status" data-testid="no-recipient">{NO_RECIPIENT_MESSAGE}</div>
        ) : (
          <select id={`${id}-agent`} value={form.agentClientId} onChange={(e) => onChange({ ...form, agentClientId: e.target.value })}>
            {recipients.map((r) => (
              <option key={r.id} value={r.id}>{r.profileName} ({r.oAuthClientId})</option>
            ))}
          </select>
        )}
      </div>

      <div className="field" role="radiogroup" aria-label="Modo">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="radio" name={`${id}-mode`} checked readOnly />
          Proposta: cada ação do agente espera aprovação de uma pessoa
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }} className="muted">
          <input type="radio" name={`${id}-mode`} disabled checked={false} readOnly />
          Direto: exige confirmação por passkey e ainda não está disponível
        </label>
      </div>

      <div className="grid-3">
        <div className="field">
          <label htmlFor={`${id}-days`}>Máximo de dias</label>
          <input
            id={`${id}-days`}
            type="number"
            min={1}
            max={kind?.maxDaysCeiling ?? 1}
            step={1}
            value={form.maxDays}
            onChange={(e) => onChange({ ...form, maxDays: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-effects`}>Efeitos{batch ? ' por loja' : ''}</label>
          <input
            id={`${id}-effects`}
            type="number"
            min={1}
            max={options.maxEffects}
            step={1}
            value={form.maxEffects}
            onChange={(e) => onChange({ ...form, maxEffects: Number(e.target.value) })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-hours`}>Validade (horas)</label>
          <input
            id={`${id}-hours`}
            type="number"
            min={1}
            max={options.maxValidForHours}
            step={1}
            value={form.validForHours}
            onChange={(e) => onChange({ ...form, validForHours: Number(e.target.value) })}
          />
        </div>
      </div>
    </fieldset>
  );
}

const STATUS_TONES: Readonly<Record<string, BadgeTone>> = {
  Open: 'success',
  Exhausted: 'neutral',
  Expired: 'warning',
  Cancelled: 'error',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleString('pt-BR');
}

interface AgentWorkOrderTableProps {
  items: AgentWorkOrderDto[];
  /** Mostra a coluna da loja (lista de todas as OS). */
  showTenant?: boolean;
  onCancel?: (item: AgentWorkOrderDto) => void;
  busyId?: string | null;
}

/** As OS, das mais novas para as mais velhas, com o cancelamento das abertas. */
export function AgentWorkOrderTable({ items, showTenant = false, onCancel, busyId = null }: AgentWorkOrderTableProps) {
  if (items.length === 0) {
    return <p className="muted">Nenhuma ordem de serviço.</p>;
  }

  return (
    <div className="table-scroll">
      <CardTable className="table" aria-label="Ordens de serviço">
        <THead>
          <Tr>
            <Th>Ordem</Th>
            {showTenant && <Th>Loja</Th>}
            <Th>Tipo</Th>
            <Th>Agente</Th>
            <Th>Máximos</Th>
            <Th>Situação</Th>
            <Th>Vence</Th>
            <Th>Criada por</Th>
            {onCancel && <Th />}
          </Tr>
        </THead>
        <TBody>
          {items.map((item) => (
            <Tr key={item.id}>
              <Td heading><span className="mono">{item.id}</span></Td>
              {showTenant && <Td label="Loja"><span className="mono">{item.tenantId ?? '—'}</span></Td>}
              <Td label="Tipo">{kindLabel(item.kind)}</Td>
              <Td label="Agente">{item.agentProfileName}</Td>
              <Td label="Máximos">
                {item.maxDays !== null ? `${item.maxDays} d · ` : ''}
                {item.effectsUsed}/{item.maxEffects} efeito(s)
              </Td>
              <Td label="Situação"><Badge tone={STATUS_TONES[item.status] ?? 'neutral'}>{statusLabel(item.status)}</Badge></Td>
              <Td label="Vence">{formatDate(item.expiresAt)}</Td>
              <Td label="Criada por">{item.createdByName}</Td>
              {onCancel && (
                <Td action>
                  {item.status === 'Open' && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={busyId === item.id}
                      onClick={() => onCancel(item)}
                    >
                      Cancelar
                    </button>
                  )}
                </Td>
              )}
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </div>
  );
}
